import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  ShoppingBag, RefreshCw, Download, Upload, Check, AlertTriangle,
  Loader2, Package, ShoppingCart, Link2, Wifi, WifiOff, DollarSign, User, Clock, Trash2, Link, Printer, MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatDateTime } from '../utils/dateFormat';

import { useStore } from '../context/StoreContext';
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShopifyIntegration = () => {
  const { storeName } = useStore();
  const { getAuthHeader } = useAuth();
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [todayOrders, setTodayOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [writeMode, setWriteMode] = useState(null);
  const [priceSync, setPriceSync] = useState(null);
  const [priceSyncBusy, setPriceSyncBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [matching, setMatching] = useState(false);
  const [updatingFromCsv, setUpdatingFromCsv] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [orderFilter, setOrderFilter] = useState('all');

  const testConnection = async () => {
    try {
      const response = await axios.get(`${API_URL}/shopify/test-connection`, { headers: getAuthHeader() });
      setConnectionStatus(response.data);
      return response.data.connected;
    } catch (error) {
      setConnectionStatus({ connected: false, message: 'Connection failed' });
      return false;
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/shopify/products`, { headers: getAuthHeader() });
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Failed to fetch products');
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/shopify/orders?fulfillment_status=unfulfilled`, { headers: getAuthHeader() });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Failed to fetch orders');
    }
  };

  const fetchTodayOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/shopify/orders?today_only=true`, { headers: getAuthHeader() });
      setTodayOrders(response.data.orders || []);
    } catch (error) {
      console.error('Failed to fetch today orders');
    }
  };

  useEffect(() => {
    const init = async () => {
      const connected = await testConnection();
      if (connected) {
        await Promise.all([fetchProducts(), fetchOrders(), fetchTodayOrders()]);
      }
      setLoading(false);
    };
    init();
  }, []);

  const handleImport = async (clearFirst = false) => {
    if (clearFirst && !window.confirm('This will clear ALL existing products in SupplyTrack and import fresh from Shopify. Continue?')) {
      return;
    }
    
    setImporting(true);
    try {
      const response = await axios.post(
        `${API_URL}/shopify/import-products?clear_first=${clearFirst}`, 
        {}, 
        { headers: getAuthHeader() }
      );
      toast.success(response.data.message);
      await fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const fetchPriceSyncStatus = async () => {
    try {
      const [wm, st] = await Promise.all([
        axios.get(`${API_URL}/shopify/write-mode`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/shopify/sync-prices/status`, { headers: getAuthHeader() }),
      ]);
      setWriteMode(wm.data);
      setPriceSync(st.data);
      return st.data;
    } catch (_) { return null; }
  };

  useEffect(() => { fetchPriceSyncStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (priceSync?.status !== 'running') return;
    const t = setInterval(fetchPriceSyncStatus, 3000);
    return () => clearInterval(t);
  }, [priceSync?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const [barcodeRecoverBusy, setBarcodeRecoverBusy] = useState(false);
  const startPriceSync = async (dryRun) => {
    if (!dryRun && !window.confirm(`Push POS prices to Shopify for every mismatched product? The website will show your till prices.`)) return;
    setPriceSyncBusy(true);
    try {
      const res = await axios.post(`${API_URL}/shopify/sync-prices?dry_run=${dryRun}`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message);
      setTimeout(fetchPriceSyncStatus, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not start price sync');
    } finally {
      setPriceSyncBusy(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API_URL}/shopify/sync-inventory`, {}, { headers: getAuthHeader() });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleDeleteImported = async () => {
    if (!window.confirm('Are you sure you want to delete ALL products that were imported from Shopify? This cannot be undone.')) {
      return;
    }
    
    setDeleting(true);
    try {
      const response = await axios.delete(`${API_URL}/shopify/imported-products`, { headers: getAuthHeader() });
      toast.success(response.data.message);
      await fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleMatchProducts = async () => {
    setMatching(true);
    try {
      const response = await axios.post(`${API_URL}/shopify/match-products`, {}, { headers: getAuthHeader() });
      toast.success(response.data.message);
      if (response.data.unmatched > 0) {
        toast.info(`${response.data.unmatched} products could not be matched (missing SKU/barcode)`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Match failed');
    } finally {
      setMatching(false);
    }
  };

  const handleUpdateShopifyFromCsv = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!window.confirm('This will update stock levels in Shopify from your CSV. Continue?')) {
      e.target.value = '';
      return;
    }
    
    setUpdatingFromCsv(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/shopify/update-inventory-from-csv`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(response.data.message);
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} products failed to update`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    } finally {
      setUpdatingFromCsv(false);
      e.target.value = '';
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-emerald-100 text-emerald-700',
      pending: 'bg-yellow-100 text-yellow-700',
      refunded: 'bg-red-100 text-red-700',
      fulfilled: 'bg-blue-100 text-blue-700',
      unfulfilled: 'bg-orange-100 text-orange-700',
    };
    return (
      <Badge className={styles[status] || 'bg-slate-100 text-slate-700'}>
        {status || 'Unknown'}
      </Badge>
    );
  };

  // Get channel badge (Online, Orewa, Eastridge)
  const getChannelBadge = (order) => {
    // Check source_name or tags for channel info
    const sourceName = order.source_name?.toLowerCase() || '';
    const tags = order.tags?.toLowerCase() || '';
    const locationName = order.location_name?.toLowerCase() || '';
    
    if (locationName.includes('orewa') || tags.includes('orewa')) {
      return <Badge className="bg-emerald-100 text-emerald-700"><MapPin className="w-3 h-3 mr-1" />{storeName('orewa')}</Badge>;
    } else if (locationName.includes('eastridge') || tags.includes('eastridge')) {
      return <Badge className="bg-blue-100 text-blue-700"><MapPin className="w-3 h-3 mr-1" />{storeName('eastridge')}</Badge>;
    } else {
      return <Badge className="bg-purple-100 text-purple-700"><ShoppingCart className="w-3 h-3 mr-1" />Online</Badge>;
    }
  };

  // Print order receipt
  const printOrderReceipt = (order) => {
    const printWindow = window.open('', '_blank', 'width=400,height=700');
    const itemsHtml = (order.line_items || []).map(item => `
      <tr>
        <td style="padding: 4px 0;">${item.quantity}x ${item.name}</td>
        <td style="text-align: right; padding: 4px 0;">$${(parseFloat(item.price) * item.quantity).toFixed(2)}</td>
      </tr>
    `).join('');
    
    printWindow.document.write(`
      <html>
      <head>
        <title>Order #${order.order_number}</title>
        <style>
          body { font-family: 'Courier New', monospace; font-size: 12px; width: 300px; margin: 0 auto; padding: 20px; }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .line { border-top: 1px dashed #000; margin: 10px 0; }
          table { width: 100%; border-collapse: collapse; }
          .total { font-size: 16px; font-weight: bold; }
        </style>
      </head>
      <body>
        <div class="center bold" style="font-size: 16px;">${localStorage.getItem('business_name') || 'SupplyTrack'}</div>
        <div class="center" style="margin-bottom: 15px;">ONLINE ORDER</div>
        <div class="line"></div>
        <div><strong>Order:</strong> #${order.order_number}</div>
        <div><strong>Date:</strong> ${formatDateTime(order.created_at)}</div>
        <div><strong>Customer:</strong> ${order.customer?.name || 'Guest'}</div>
        ${order.customer?.email ? `<div><strong>Email:</strong> ${order.customer.email}</div>` : ''}
        ${order.shipping_address ? `
          <div class="line"></div>
          <div><strong>Ship To:</strong></div>
          <div>${order.shipping_address.name || ''}</div>
          <div>${order.shipping_address.address1 || ''}</div>
          ${order.shipping_address.address2 ? `<div>${order.shipping_address.address2}</div>` : ''}
          <div>${order.shipping_address.city || ''} ${order.shipping_address.zip || ''}</div>
        ` : ''}
        <div class="line"></div>
        <table>
          <tbody>
            ${itemsHtml}
          </tbody>
        </table>
        <div class="line"></div>
        <table>
          <tr>
            <td>Subtotal:</td>
            <td style="text-align: right;">$${parseFloat(order.subtotal_price || 0).toFixed(2)}</td>
          </tr>
          <tr>
            <td>Shipping:</td>
            <td style="text-align: right;">$${parseFloat(order.total_shipping_price_set?.shop_money?.amount || 0).toFixed(2)}</td>
          </tr>
          <tr>
            <td>Tax:</td>
            <td style="text-align: right;">$${parseFloat(order.total_tax || 0).toFixed(2)}</td>
          </tr>
        </table>
        <div class="line"></div>
        <table>
          <tr class="total">
            <td>TOTAL:</td>
            <td style="text-align: right;">$${parseFloat(order.total_price).toFixed(2)} ${order.currency}</td>
          </tr>
        </table>
        <div class="line"></div>
        <div class="center">
          <div><strong>Payment:</strong> ${order.financial_status?.toUpperCase()}</div>
          <div><strong>Fulfillment:</strong> ${(order.fulfillment_status || 'UNFULFILLED').toUpperCase()}</div>
        </div>
        <div class="line"></div>
        <div class="center" style="margin-top: 15px;">Thank you for your order!</div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // Calculate totals - only count paid/completed orders for revenue
  const paidOrders = orders.filter(o => o.financial_status === 'paid');
  const totalRevenue = paidOrders.reduce((sum, o) => sum + parseFloat(o.total_price || 0), 0);
  
  // Today's sales from the dedicated fetch
  const todayRevenue = todayOrders.reduce((sum, o) => sum + parseFloat(o.total_price || 0), 0);
  
  const pendingOrders = orders.filter(o => 
    o.financial_status !== 'refunded' && 
    o.financial_status !== 'voided' &&
    (o.fulfillment_status === null || o.fulfillment_status === 'partial' || o.fulfillment_status === '')
  ).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shopify-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            Ecommerce / Online Sales
          </h1>
          <p className="text-slate-500 mt-1">View your Shopify orders and manage online inventory</p>
        </div>
        <Button variant="outline" onClick={() => { fetchOrders(); fetchProducts(); }}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh Data
        </Button>
      </div>

      {/* Connection Status */}
      <Card className={`border-2 ${connectionStatus?.connected ? (connectionStatus?.missing_permissions?.length > 0 ? 'border-yellow-200 bg-yellow-50' : 'border-emerald-200 bg-emerald-50') : 'border-red-200 bg-red-50'}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {connectionStatus?.connected ? (
                connectionStatus?.missing_permissions?.length > 0 ? (
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  </div>
                ) : (
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Wifi className="w-5 h-5 text-emerald-600" />
                  </div>
                )
              ) : (
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <WifiOff className="w-5 h-5 text-red-600" />
                </div>
              )}
              <div>
                <h3 className="font-bold">
                  {connectionStatus?.connected 
                    ? (connectionStatus?.missing_permissions?.length > 0 
                        ? 'Connected - Permission Issues' 
                        : 'Connected to Shopify')
                    : 'Not Connected'}
                </h3>
                {connectionStatus?.connected ? (
                  <div>
                    <p className="text-sm text-slate-600">
                      {connectionStatus.store_name} • {connectionStatus.store_domain}
                    </p>
                    {connectionStatus?.warning && (
                      <p className="text-sm text-yellow-700 mt-1 font-medium">
                        ⚠️ {connectionStatus.warning}
                      </p>
                    )}
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-red-600 font-medium">{connectionStatus?.message}</p>
                    {connectionStatus?.detail && (
                      <p className="text-sm text-slate-600 mt-2">{connectionStatus.detail}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                size="sm"
                onClick={handleSync} 
                disabled={syncing || !connectionStatus?.connected}
                title="Push SupplyTrack stock to Shopify"
              >
                {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                <span className="ml-2 hidden sm:inline">Sync Inventory</span>
              </Button>
              <Button 
                variant="outline"
                size="sm"
                onClick={handleMatchProducts} 
                disabled={matching || !connectionStatus?.connected}
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                title="Match SupplyTrack products to Shopify by SKU"
              >
                {matching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link className="w-4 h-4" />}
                <span className="ml-2 hidden sm:inline">Match by SKU</span>
              </Button>
              
              {/* Update Shopify from CSV */}
              <input 
                type="file" 
                accept=".csv" 
                onChange={handleUpdateShopifyFromCsv}
                className="hidden"
                id="shopify-csv-update"
                disabled={updatingFromCsv || !connectionStatus?.connected}
              />
              <label htmlFor="shopify-csv-update">
                <Button 
                  asChild
                  variant="outline"
                  size="sm"
                  className="text-green-600 border-green-200 hover:bg-green-50 cursor-pointer"
                  disabled={updatingFromCsv || !connectionStatus?.connected}
                >
                  <span>
                    {updatingFromCsv ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    <span className="ml-2 hidden sm:inline">Fix Stock from CSV</span>
                  </span>
                </Button>
              </label>
            </div>
          </div>
          
          {/* Permission Details */}
          {connectionStatus?.connected && connectionStatus?.permissions && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <p className="text-xs font-bold uppercase text-slate-500 mb-2">API Permissions</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(connectionStatus.permissions).map(([perm, granted]) => (
                  <Badge 
                    key={perm} 
                    className={granted ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}
                  >
                    {granted ? <Check className="w-3 h-3 mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
                    {perm}
                  </Badge>
                ))}
              </div>
              {connectionStatus?.missing_permissions?.length > 0 && (
                <p className="mt-3 text-sm text-slate-600">
                  To fix: Go to your Shopify Admin → Settings → Apps and sales channels → Develop apps → 
                  Select your app → Configure Admin API scopes → Enable the missing permissions → Save
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {connectionStatus?.connected && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className={`border rounded-sm cursor-pointer transition-all ${orderFilter === 'all' ? 'ring-2 ring-emerald-400' : 'hover:shadow-md'}`} onClick={() => setOrderFilter('all')}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold" data-testid="today-revenue">${todayRevenue.toFixed(2)}</p>
                  <p className="text-sm text-slate-500">Today's Sales ({todayOrders.length} orders)</p>
                </div>
              </CardContent>
            </Card>
            <Card className={`border rounded-sm cursor-pointer transition-all ${orderFilter === 'all' ? 'ring-2 ring-blue-400' : 'hover:shadow-md'}`} onClick={() => setOrderFilter('all')}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <ShoppingCart className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{orders.length}</p>
                  <p className="text-sm text-slate-500">Total Orders</p>
                </div>
              </CardContent>
            </Card>
            <Card className={`border rounded-sm cursor-pointer transition-all ${orderFilter === 'pending' ? 'ring-2 ring-yellow-400' : 'hover:shadow-md'}`} onClick={() => setOrderFilter('pending')} data-testid="pending-orders-card">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{pendingOrders}</p>
                  <p className="text-sm text-slate-500">Pending Fulfillment</p>
                </div>
              </CardContent>
            </Card>
            <Card className={`border rounded-sm cursor-pointer transition-all ${orderFilter === 'all' ? '' : 'hover:shadow-md'}`} onClick={() => setOrderFilter('all')}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Package className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{products.length}</p>
                  <p className="text-sm text-slate-500">Products</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Price sync — POS is the source of truth; the website must show till prices */}
          <Card className="border-emerald-200" data-testid="price-sync-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                <DollarSign className="w-5 h-5 text-emerald-600" /> Price Sync — POS → Shopify
                {writeMode && (
                  <Badge className={writeMode.live ? 'bg-emerald-600' : 'bg-amber-500'} data-testid="shopify-write-mode">
                    {writeMode.live ? 'LIVE — changes push to Shopify' : 'PREVIEW — Shopify writes are skipped here'}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Every price change at the till or in Inventory is pushed to Shopify automatically. Use this to find and fix any that drifted.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => startPriceSync(true)} disabled={priceSyncBusy || priceSync?.status === 'running'} data-testid="price-check-btn">
                  {priceSync?.status === 'running' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />} Check prices vs Shopify
                </Button>
                <Button size="sm" onClick={() => startPriceSync(false)} disabled={priceSyncBusy || priceSync?.status === 'running' || !writeMode?.live} className="bg-emerald-600 hover:bg-emerald-700" data-testid="price-push-btn">
                  <Upload className="w-4 h-4 mr-1" /> Push POS prices to Shopify
                </Button>
                <Button size="sm" variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-50" disabled={barcodeRecoverBusy} data-testid="recover-barcodes-btn"
                  onClick={async () => {
                    if (!window.confirm('Look up every product that has a system 99… barcode and is linked to Shopify, and attach the real barcode from Shopify as an extra barcode?\n\nNothing is removed — both barcodes will scan afterwards. Takes about a second per product.')) return;
                    setBarcodeRecoverBusy(true);
                    try {
                      const r = await axios.post(`${API_URL}/products/barcodes/recover-from-shopify`, {}, { headers: getAuthHeader() });
                      const d = r.data;
                      window.alert(`Checked ${d.checked} products.\n\nReal barcodes recovered: ${d.recovered}\nAlready linked: ${d.already_linked}\nShopify has no barcode: ${d.shopify_has_no_barcode}\nFailed: ${d.failed}` +
                        (d.clashes?.length ? `\n\nSkipped (barcode already on another product): ${d.clashes.length}` : '') +
                        (d.details?.length ? `\n\n${d.details.slice(0, 10).map(x => `• ${x.product}: ${x.recovered_barcode}`).join('\n')}` : ''));
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Recovery failed');
                    } finally {
                      setBarcodeRecoverBusy(false);
                    }
                  }}>
                  {barcodeRecoverBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />} Recover real barcodes from Shopify
                </Button>
              </div>
              {priceSync && priceSync.status !== 'idle' && (
                <div className="text-sm" data-testid="price-sync-status">
                  <p className="font-medium">
                    {priceSync.status === 'running' ? 'Checking…' : priceSync.status === 'error' ? `Error: ${priceSync.error}` : 'Done'} —
                    {' '}{priceSync.checked}/{priceSync.total} checked · <span className={priceSync.mismatched ? 'text-red-600 font-bold' : 'text-emerald-700'}>{priceSync.mismatched} price mismatches</span>
                    {!priceSync.dry_run && <> · {priceSync.pushed} pushed · {priceSync.failed} failed</>}
                    {priceSync.finished_at && <span className="text-slate-400"> · {formatDateTime(priceSync.finished_at)}</span>}
                  </p>
                  {(priceSync.samples || []).length > 0 && (
                    <div className="mt-2 border rounded-md max-h-64 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 sticky top-0"><tr><th className="text-left px-2 py-1">Product</th><th className="text-right px-2 py-1">POS</th><th className="text-right px-2 py-1">Shopify</th></tr></thead>
                        <tbody>
                          {priceSync.samples.map(m => (
                            <tr key={m.id} className="border-t" data-testid={`price-mismatch-${m.id}`}>
                              <td className="px-2 py-1 truncate max-w-[420px]">{m.name}</td>
                              <td className="px-2 py-1 text-right font-semibold text-emerald-700">${m.pos_price.toFixed(2)}</td>
                              <td className="px-2 py-1 text-right text-red-600">${m.shopify_price.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {priceSync.mismatched > priceSync.samples.length && <p className="text-[11px] text-slate-500 px-2 py-1">Showing first {priceSync.samples.length} of {priceSync.mismatched}</p>}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tabs - Orders first */}
          <Tabs defaultValue="orders" className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="orders" data-testid="orders-tab">
                <ShoppingCart className="w-4 h-4 mr-2" /> Orders ({orders.length})
              </TabsTrigger>
              <TabsTrigger value="products" data-testid="products-tab">
                <Package className="w-4 h-4 mr-2" /> Products ({products.length})
              </TabsTrigger>
            </TabsList>

            {/* Orders Tab */}
            <TabsContent value="orders" className="mt-6">
              <Card className="border rounded-sm">
                <CardHeader className="border-b bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
                        <ShoppingCart className="w-5 h-5" /> {orderFilter === 'pending' ? 'Pending Orders' : 'Shopify Orders'}
                      </CardTitle>
                      <CardDescription>
                        {orderFilter === 'pending' 
                          ? `${pendingOrders} orders awaiting fulfillment` 
                          : 'Your online orders from Shopify'}
                      </CardDescription>
                    </div>
                    {orderFilter === 'pending' && (
                      <Button variant="outline" size="sm" onClick={() => setOrderFilter('all')}>
                        Show All Orders
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[500px]">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-heading font-bold uppercase text-xs">Order #</TableHead>
                          <TableHead className="font-heading font-bold uppercase text-xs">Customer</TableHead>
                          <TableHead className="font-heading font-bold uppercase text-xs">Items</TableHead>
                          <TableHead className="font-heading font-bold uppercase text-xs text-right">Total</TableHead>
                          <TableHead className="font-heading font-bold uppercase text-xs">Payment</TableHead>
                          <TableHead className="font-heading font-bold uppercase text-xs">Fulfillment</TableHead>
                          <TableHead className="font-heading font-bold uppercase text-xs">Date</TableHead>
                          <TableHead className="font-heading font-bold uppercase text-xs">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(() => {
                          const displayOrders = orderFilter === 'pending' 
                            ? orders.filter(o => 
                                o.financial_status !== 'refunded' && 
                                o.financial_status !== 'voided' &&
                                (o.fulfillment_status === null || o.fulfillment_status === 'partial' || o.fulfillment_status === '')
                              )
                            : orders;
                          
                          if (displayOrders.length === 0) {
                            return (
                              <TableRow>
                                <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                                  <ShoppingCart className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                                  {orderFilter === 'pending' ? 'No pending orders' : 'No orders found'}
                                </TableCell>
                              </TableRow>
                            );
                          }
                          
                          return displayOrders.map((order) => (
                            <TableRow 
                              key={order.id} 
                              data-testid={`order-row-${order.id}`}
                              className={`cursor-pointer hover:bg-slate-50 ${order.fulfillment_status !== 'fulfilled' ? 'bg-orange-50/50' : ''}`}
                            >
                              <TableCell className="font-bold">#{order.order_number}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <User className="w-4 h-4 text-slate-400" />
                                  <div>
                                    <p className="font-medium">{order.customer?.name || 'Guest'}</p>
                                    {order.customer?.email && (
                                      <p className="text-xs text-slate-500">{order.customer.email}</p>
                                    )}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="space-y-1">
                                  {order.line_items?.slice(0, 2).map((item, idx) => (
                                    <p key={idx} className="text-sm">
                                      {item.quantity}x {item.name?.substring(0, 30)}{item.name?.length > 30 ? '...' : ''}
                                    </p>
                                  ))}
                                  {order.line_items?.length > 2 && (
                                    <p className="text-xs text-slate-400">+{order.line_items.length - 2} more</p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-bold">
                                ${parseFloat(order.total_price).toFixed(2)} {order.currency}
                              </TableCell>
                              <TableCell>{getStatusBadge(order.financial_status)}</TableCell>
                              <TableCell>{getStatusBadge(order.fulfillment_status || 'unfulfilled')}</TableCell>
                              <TableCell className="text-slate-500 text-sm">
                                {formatDate(order.created_at)}
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    printOrderReceipt(order);
                                  }}
                                  data-testid={`print-order-${order.id}`}
                                >
                                  <Printer className="w-4 h-4 mr-1" /> Print
                                </Button>
                              </TableCell>
                            </TableRow>
                          ));
                        })()}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Products Tab */}
            <TabsContent value="products" className="mt-6">
              <Card className="border rounded-sm">
                <CardHeader className="border-b bg-slate-50">
                  <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5" /> Shopify Products
                  </CardTitle>
                  <CardDescription>Products synced from your Shopify store</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[500px]">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-heading font-bold uppercase text-xs">Product</TableHead>
                          <TableHead className="font-heading font-bold uppercase text-xs">Vendor</TableHead>
                          <TableHead className="font-heading font-bold uppercase text-xs">Variants</TableHead>
                          <TableHead className="font-heading font-bold uppercase text-xs">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {products.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-12 text-slate-500">
                              <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                              No products found in Shopify
                            </TableCell>
                          </TableRow>
                        ) : (
                          products.map((product) => (
                            <TableRow key={product.id}>
                              <TableCell>
                                <div>
                                  <p className="font-medium">{product.title}</p>
                                  <p className="text-xs text-slate-500">{product.handle}</p>
                                </div>
                              </TableCell>
                              <TableCell>{product.vendor || '-'}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{product.variants?.length || 0} variants</Badge>
                              </TableCell>
                              <TableCell>
                                <Badge className={product.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100'}>
                                  {product.status}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default ShopifyIntegration;
